/**
 * tireSizeParser.ts — Smart tire size search parser + scorer
 *
 * Pure functions, no React dependencies. Parses user input (structured or raw
 * digits), decomposes tire sizes into {width, ratio, diameter}, scores candidates
 * from the master list, and returns sorted results.
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ParsedIntent {
  width?: string;       // e.g. "225"
  ratio?: string;       // e.g. "50"
  diameter?: string;    // e.g. "17"
  flotationWidth?: string; // e.g. "35" for flotation sizes
  flotationRatio?: string; // e.g. "12.5"
  isFlotation?: boolean;
}

interface DecomposedSize {
  width: string;
  ratio: string;
  diameter: string;
  isFlotation: boolean;
  raw: string;
}

export interface TireSizeResult {
  size: string;
  score: number;
  diameter: number;
}

export interface TireSizeGroup {
  diameter: number;
  sizes: TireSizeResult[];
}

// ---------------------------------------------------------------------------
// Decompose a tire size string into components
// ---------------------------------------------------------------------------

function decomposeSize(size: string): DecomposedSize | null {
  // Metric: "225/50R17"
  const metric = size.match(/^(\d{3})\/(\d{2})R(\d{2})$/i);
  if (metric) {
    return { width: metric[1], ratio: metric[2], diameter: metric[3], isFlotation: false, raw: size };
  }
  // Flotation: "35x12.5R17" or "35x12.5-17"
  const flotation = size.match(/^(\d{2})x(\d+\.?\d*)[R-](\d{2})$/i);
  if (flotation) {
    return { width: flotation[1], ratio: flotation[2], diameter: flotation[3], isFlotation: true, raw: size };
  }
  return null;
}

// ---------------------------------------------------------------------------
// Parse user query into intent
// ---------------------------------------------------------------------------

function parseStructuredInput(query: string): ParsedIntent | null {
  const q = query.trim();

  // Full or partial flotation: "35x12.5R17", "35x12.5-17", "35x12.5", "35x"
  const flotationFull = q.match(/^(\d{2})x(\d+\.?\d*)[R-](\d{1,2})$/i);
  if (flotationFull) {
    return { flotationWidth: flotationFull[1], flotationRatio: flotationFull[2], diameter: flotationFull[3], isFlotation: true };
  }
  const flotationPartialRatio = q.match(/^(\d{2})x(\d+\.?\d*)$/i);
  if (flotationPartialRatio) {
    return { flotationWidth: flotationPartialRatio[1], flotationRatio: flotationPartialRatio[2], isFlotation: true };
  }
  const flotationWidthOnly = q.match(/^(\d{2})x$/i);
  if (flotationWidthOnly) {
    return { flotationWidth: flotationWidthOnly[1], isFlotation: true };
  }

  // Metric structured: "225/50R17", "225/50", "225/", "R17", "/50R17"
  const fullMetric = q.match(/^(\d{3})\/(\d{1,2})R(\d{1,2})$/i);
  if (fullMetric) {
    return { width: fullMetric[1], ratio: fullMetric[2], diameter: fullMetric[3] };
  }
  const widthRatio = q.match(/^(\d{3})\/(\d{1,2})$/i);
  if (widthRatio) {
    return { width: widthRatio[1], ratio: widthRatio[2] };
  }
  const widthSlash = q.match(/^(\d{3})\/$/);
  if (widthSlash) {
    return { width: widthSlash[1] };
  }
  const rDiameter = q.match(/^R(\d{1,2})$/i);
  if (rDiameter) {
    return { diameter: rDiameter[1] };
  }
  const slashRatioDiameter = q.match(/^\/(\d{1,2})R(\d{1,2})$/i);
  if (slashRatioDiameter) {
    return { ratio: slashRatioDiameter[1], diameter: slashRatioDiameter[2] };
  }
  const slashRatio = q.match(/^\/(\d{1,2})$/);
  if (slashRatio) {
    return { ratio: slashRatio[1] };
  }

  return null;
}

function parseRawDigits(query: string): ParsedIntent | null {
  const q = query.trim();
  if (!/^\d+$/.test(q)) return null;

  const len = q.length;

  if (len === 1) return null; // Too short

  if (len === 2) {
    const val = parseInt(q, 10);
    if (val >= 10 && val <= 24) return { diameter: q };   // Diameter-first
    if (val >= 25 && val <= 85) return { ratio: q };       // Ratio
    return null; // Outside valid range
  }

  if (len === 3) {
    return { width: q }; // 3 digits = always width
  }

  // Positional parsing: 3 (width) + 1-2 (ratio) + 0-2 (diameter)
  if (len === 4) {
    // width=first3, ratio starts with last1
    return { width: q.slice(0, 3), ratio: q.slice(3) };
  }

  if (len === 5) {
    // width=first3, ratio=next2
    return { width: q.slice(0, 3), ratio: q.slice(3, 5) };
  }

  if (len === 6) {
    // width=first3, ratio=next2, diameter starts with last1
    return { width: q.slice(0, 3), ratio: q.slice(3, 5), diameter: q.slice(5) };
  }

  if (len === 7) {
    // width=first3, ratio=next2, diameter=last2 — full match
    return { width: q.slice(0, 3), ratio: q.slice(3, 5), diameter: q.slice(5, 7) };
  }

  return null; // Too long
}

function parseQuery(query: string): ParsedIntent | null {
  const q = query.trim();
  if (!q) return null;

  // Check for structured delimiters first
  if (/[/xXrR]/.test(q)) {
    const structured = parseStructuredInput(q);
    if (structured) return structured;
  }

  // Decimal number without delimiters (e.g. "12.", "12.5", "12.50") → flotation ratio
  const decimalMatch = q.match(/^(\d+\.\d*)$/);
  if (decimalMatch) {
    return { flotationRatio: decimalMatch[1], isFlotation: true };
  }

  return parseRawDigits(q);
}

// ---------------------------------------------------------------------------
// Scoring
// ---------------------------------------------------------------------------

function scoreCandidate(decomposed: DecomposedSize, intent: ParsedIntent): number | null {
  // Flotation intent should only match flotation sizes
  if (intent.isFlotation) {
    if (!decomposed.isFlotation) return null;
    return scoreFlotation(decomposed, intent);
  }

  // For non-flotation intents, allow ambiguous queries to also match flotation
  if (decomposed.isFlotation) {
    const diameterOnly = !!intent.diameter && !intent.width && !intent.ratio;
    const ratioOnly = !intent.width && !intent.diameter && !!intent.ratio;

    if (diameterOnly) {
      // "15" should match both 205/65R15 and 33x12.5R15 (by diameter)
      // "12" should also match 33x12.5R15 (by ratio prefix, since 12.x is a common flotation ratio)
      let s = 0;
      if (decomposed.diameter === intent.diameter) { s += 120; }
      else if (decomposed.diameter.startsWith(intent.diameter!)) { s += 35; }
      else if (decomposed.ratio.startsWith(intent.diameter!)) { s += 20; }
      else { return null; }
      return s + 10;
    }

    if (ratioOnly) {
      // "33" could be flotation width (33x12.5R15) — try matching by width
      const val = parseInt(intent.ratio!, 10);
      if (val >= 26 && val <= 44) {
        let s = 0;
        if (decomposed.width === intent.ratio) { s += 100; }
        else if (decomposed.width.startsWith(intent.ratio!)) { s += 30; }
        else { return null; }
        return s + 10;
      }
    }

    return null; // Non-ambiguous metric intent → reject flotation
  }

  let score = 0;
  let fieldsMatched = 0;

  // Width
  if (intent.width) {
    if (decomposed.width === intent.width) {
      score += 100;
      fieldsMatched++;
    } else if (decomposed.width.startsWith(intent.width)) {
      score += 30;
      fieldsMatched++;
    } else if (intent.width.length >= 2 && decomposed.width.slice(0, 2) === intent.width.slice(0, 2)) {
      // Near-prefix: "240" matches "245" (first 2 digits shared) — weaker signal
      score += 15;
      fieldsMatched++;
    } else {
      return null; // Mismatch
    }
  }

  // Ratio
  if (intent.ratio) {
    if (decomposed.ratio === intent.ratio) {
      score += 80;
      fieldsMatched++;
    } else if (decomposed.ratio.startsWith(intent.ratio)) {
      score += 25;
      fieldsMatched++;
    } else {
      return null; // Mismatch
    }
  }

  // Diameter
  if (intent.diameter) {
    if (decomposed.diameter === intent.diameter) {
      score += 120;
      fieldsMatched++;
    } else if (decomposed.diameter.startsWith(intent.diameter)) {
      score += 35;
      fieldsMatched++;
    } else {
      return null; // Mismatch
    }
  }

  // Bonus for more fields matched
  score += fieldsMatched * 10;

  return score;
}

function scoreFlotation(decomposed: DecomposedSize, intent: ParsedIntent): number | null {
  let score = 0;
  let fieldsMatched = 0;

  if (intent.flotationWidth) {
    if (decomposed.width === intent.flotationWidth) {
      score += 100;
      fieldsMatched++;
    } else if (decomposed.width.startsWith(intent.flotationWidth)) {
      score += 30;
      fieldsMatched++;
    } else {
      return null;
    }
  }

  if (intent.flotationRatio) {
    if (decomposed.ratio === intent.flotationRatio) {
      score += 80;
      fieldsMatched++;
    } else if (decomposed.ratio.startsWith(intent.flotationRatio)) {
      score += 25;
      fieldsMatched++;
    } else {
      return null;
    }
  }

  if (intent.diameter) {
    if (decomposed.diameter === intent.diameter) {
      score += 120;
      fieldsMatched++;
    } else if (decomposed.diameter.startsWith(intent.diameter)) {
      score += 35;
      fieldsMatched++;
    } else {
      return null;
    }
  }

  score += fieldsMatched * 10;
  return score;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Search tire sizes from the master list against a user query.
 * Returns scored, sorted results excluding sizes in the excludeSet.
 */
export function searchTireSizes(
  query: string,
  masterList: string[] | null,
  excludeSet: Set<string>,
): TireSizeResult[] {
  if (!masterList || !query.trim()) return [];

  const intent = parseQuery(query);
  if (!intent) return [];

  const results: TireSizeResult[] = [];

  for (const size of masterList) {
    if (excludeSet.has(size)) continue;

    const decomposed = decomposeSize(size);
    if (!decomposed) continue;

    const score = scoreCandidate(decomposed, intent);
    if (score === null) continue;

    results.push({
      size,
      score,
      diameter: parseInt(decomposed.diameter, 10),
    });
  }

  // Sort: metric first, then flotation; within each group: score desc → diameter asc → size asc
  const isFlotation = (s: string) => s.includes('x');
  results.sort((a, b) => {
    const aFlot = isFlotation(a.size) ? 1 : 0;
    const bFlot = isFlotation(b.size) ? 1 : 0;
    return aFlot - bFlot || b.score - a.score || a.diameter - b.diameter || a.size.localeCompare(b.size);
  });

  return results;
}

/**
 * Structural validation — does this look like a valid tire size format?
 * Allows sizes not in catalog.
 */
export function isValidTireSizeFormat(query: string): boolean {
  const q = query.trim();
  // Metric: 3digits/2digitsR2digits
  if (/^\d{3}\/\d{2}R\d{2}$/i.test(q)) return true;
  // Flotation: 2digits x digits(.digits) R|hyphen 2digits
  if (/^\d{2}x\d+\.?\d*[R-]\d{2}$/i.test(q)) return true;
  return false;
}

/**
 * Normalize flotation hyphen format to R format (e.g. "35x12.5-20" → "35x12.5R20").
 */
function normalizeFlotationHyphen(size: string): string {
  return size.replace(/^(\d{2}x\d+\.?\d*)-(\d{2})$/i, '$1R$2');
}

/**
 * Check if a size string exists in the master list.
 * Normalizes hyphen-format flotation sizes before comparing.
 */
export function isInMasterList(size: string, masterList: string[] | null): boolean {
  if (!masterList) return false;
  const normalized = normalizeFlotationHyphen(size);
  return masterList.includes(normalized);
}

/**
 * Group results by rim diameter for sectioned display.
 */
export function groupByDiameter(results: TireSizeResult[]): TireSizeGroup[] {
  const map = new Map<number, TireSizeResult[]>();
  for (const r of results) {
    const group = map.get(r.diameter);
    if (group) {
      group.push(r);
    } else {
      map.set(r.diameter, [r]);
    }
  }

  return Array.from(map.entries())
    .sort(([a], [b]) => a - b)
    .map(([diameter, sizes]) => ({ diameter, sizes }));
}
